import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { Fragment, useMemo, useState } from 'react';
import { BidBreadcrumbs } from '@/features/bids/components/bid-breadcrumbs';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAtom } from 'jotai';
import { BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Header, HeaderChatButton, HeaderBackButton } from '@/ui/components/header';
import { IndexingStatusAlerts } from '@/features/documents/components/indexing-status-alerts';
import {
  Alert01Icon,
  Copy01Icon,
  InformationCircleIcon,
  Refresh01Icon,
  SearchingIcon,
} from '@hugeicons-pro/core-stroke-rounded';
import { Icon } from '@/ui/components/icon';
import {
  MainActions,
  MainActionsEnd,
  MainActionsSearchInput,
  MainContainer,
  MainHeaderBackButton,
  MainScrollArea,
} from '@/ui/components/main';
import { H1, H2 } from '@/ui/components/heading';
import {
  ActionBar,
  ActionBarGroup,
  ActionBarItem,
  ActionBarSelection,
  ActionBarSeparator,
  ActionBarXClose,
} from '@/ui/components/action-bar';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogPopup,
} from '@/ui/components/alert-dialog';
import { Badge } from '@/ui/components/badge';
import { FileIcon } from '@/ui/components/file-icon';
import { ScoreDial } from '@/ui/components/score-dial';
import { useDebouncedQuery } from '@/ui/hooks/use-debounced-query';
import { type ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Table, TableBody, TableRow } from '@/ui/components/table';
import { DataTableHeader, DataTableCell } from '@/ui/components/data-table';
import { CvRoleMenu } from '@/features/bid-cvs/components/cv-role-menu';
import { CvAnalysisFilterMenu } from '@/features/bid-cvs/components/cv-analysis-filter-menu';
import { CvAnalysisDisplayMenu } from '@/features/bid-cvs/components/cv-analysis-display-menu';
import { type CvAnalysisFilter } from '@/features/bid-cvs/filters';
import { cvFilterAtom } from '@/features/bid-cvs/store';
import type {
  CvAssessmentCategory,
  CvOverviewRow,
  CvRoleAssignments,
  DuplicateRoleGroup,
} from '@/features/bid-cvs/types';
import { useSyncedCvRoleAssignments } from '@/features/bid-cvs/helpers';
import { Spinner } from '@/ui/components/spinner';
import { Card } from '@/ui/components/card';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyMedia,
  EmptyTitle,
} from '@/ui/components/empty';
import { Progress, ProgressTrack, ProgressValue } from '@/ui/components/progress';
import { useRunCvAnalysis } from '@/features/bid-cvs/mutations';
import { bidCvDetailsQuery } from '@/features/bid-cvs/queries';
import {
  buildAssignmentPayload,
  buildCvOverviewRows,
  countPendingChanges,
  countScoredCvAssessments,
  findDuplicateRoleGroups,
  getAllBidCvs,
  getCvAssignmentState,
  getCvDisplayName,
  getInitialAssignments,
  getNavigationPositionForBidDoc,
  getOverviewRowCvName,
  getOverviewRowRoleTitle,
  getRoleCategories,
  hasConflictingDocumentRoles,
  sortCvOverviewRows,
  UNCATEGORIZED_CATEGORY_ID,
} from '@/features/bid-cvs/cv-role-assignments';
import {
  AISummaryAlert,
  AISummaryAlertAskAIButton,
  AISummaryAlertCopyButton,
  AISummaryAlertDescription,
  AISummaryAlertTitle,
} from '@/ui/components/ai-summary-alert';
import { Alert, AlertDescription } from '@/ui/components/alert';
import { CircularProgress } from '@/ui/components/circular-progress';
import { getBidAssessmentColor } from '@/features/bids/utils';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Button } from '@/ui/components/button';
import { CvAssessmentStatus } from '@/features/bid-cvs/constants';
import { entityDocumentsQuery, useDocumentsIndexingStatus } from '@/features/documents/queries';
import { useProjectView } from '@/features/projects/helpers';

export const Route = createFileRoute(
  '/_authed/project/$projectId/workspaces/$workspaceId/bids/$bidId/_layout/cv-analysis/',
)({
  component: RouteComponent,
});

const rowMatchesFilters = (
  row: CvOverviewRow,
  filters: CvAnalysisFilter[],
  duplicateCvKeys: Set<string>,
  pending: CvRoleAssignments,
  allCategories: CvAssessmentCategory[],
) => {
  if (filters.length === 0) return true;

  if (filters.includes('unassigned') && row.kind === 'gap') return true;

  if (row.kind === 'cv') {
    const score = getCvAssignmentState(row.cvKey, pending, allCategories).score;
    if (filters.includes('other') && row.assignedCategoryId === UNCATEGORIZED_CATEGORY_ID)
      return true;
    if (filters.includes('scored') && score != null) return true;
    if (filters.includes('unscored') && score == null) return true;
    if (filters.includes('duplicate') && duplicateCvKeys.has(row.cvKey)) return true;
  }

  return false;
};

function RouteComponent() {
  const isClientView = useProjectView() === 'client';
  const { projectId, bidId, workspaceId } = Route.useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery(
    bidCvDetailsQuery(bidId),
  );
  const { data: documents } = useQuery(entityDocumentsQuery(bidId, 'bid'));
  const { areDocumentsIndexed: bidReady } = useDocumentsIndexingStatus({
    documentType: 'bid',
    id: bidId,
  });
  const { areDocumentsIndexed: tenderReady, hasNoDocuments: tenderEmpty } =
    useDocumentsIndexingStatus({
      documentType: 'tenderPackage',
      id: workspaceId,
    });
  const canRun =
    bidReady &&
    tenderReady &&
    !tenderEmpty &&
    documents?.some((document) => document.documentCategory === 'CV');
  const runCvAnalysis = useRunCvAnalysis();
  const { allCategories, pending, setPending } = useSyncedCvRoleAssignments();
  const [rerunOpen, setRerunOpen] = useState(false);
  const [cvFilter, setCvFilter] = useAtom(cvFilterAtom);
  const { searchQuery, setSearchQuery, debouncedSearchQuery } = useDebouncedQuery();

  const isProcessing =
    data?.status === CvAssessmentStatus.IN_PROGRESS ||
    (runCvAnalysis.isPending && data?.status !== CvAssessmentStatus.COMPLETED);
  const isFailed =
    data?.status === CvAssessmentStatus.FAILED ||
    data?.status === CvAssessmentStatus.LEGACY_FAILED ||
    data?.status === CvAssessmentStatus.RFP_NOT_EXHAUSTIVE ||
    data?.status === CvAssessmentStatus.BID_NOT_EXHAUSTIVE;
  const notFound = isAxiosError(error) && error.response?.status === 404;
  const isLoadError = isError && !notFound;
  const isNotStarted =
    !isLoadError && (data?.status === CvAssessmentStatus.NOT_STARTED || notFound);
  const canStartAnalysis = !isLoadError && (isFailed || isNotStarted);
  const failureMessage =
    data?.status === CvAssessmentStatus.RFP_NOT_EXHAUSTIVE
      ? {
          en: 'If possible, add the relevant information to the RFP and try again.',
          nb: 'Om mulig, legg til relevant informasjon i anbudsforespørselen og prøv på nytt.',
        }
      : data?.status === CvAssessmentStatus.BID_NOT_EXHAUSTIVE
        ? isClientView
          ? {
              en: 'The bid is missing relevant information. Upload any missing bid documents and try again.',
              nb: 'Tilbudet mangler relevant informasjon. Last opp eventuelle manglende tilbudsdokumenter og prøv på nytt.',
            }
          : {
              en: 'If possible, add relevant information to the Bid and try again.',
              nb: 'Om mulig, legg til relevant informasjon i anbudet og prøv på nytt.',
            }
        : {
            en: 'CV analysis could not be completed. Try again. If it keeps failing, check the uploaded CVs or contact support.',
            nb: 'CV-analysen kunne ikke fullføres. Prøv igjen. Hvis den fortsatt mislykkes, sjekk de opplastede CV-ene eller kontakt kundestøtte.',
          };
  const versionStatus = data?.versionStatus;

  const scoredCvCounts = countScoredCvAssessments(allCategories);

  const hasTotalScore = data?.totalScore != null;
  const showScoredCount = scoredCvCounts.total > 0;

  const initialAssignments = useMemo(() => getInitialAssignments(allCategories), [allCategories]);

  const allRows = useMemo(
    () => buildCvOverviewRows(allCategories, pending),
    [allCategories, pending],
  );

  const duplicateRoleGroups = useMemo(
    () => findDuplicateRoleGroups(pending, allCategories),
    [pending, allCategories],
  );

  const duplicateCvKeys = useMemo(
    () => new Set(duplicateRoleGroups.flatMap((group) => group.cvKeys)),
    [duplicateRoleGroups],
  );

  const hasDuplicates = duplicateRoleGroups.length > 0;
  const roleEditingBlocked = hasConflictingDocumentRoles(allCategories);

  const anonymousLabel = t({ en: 'Anonymous', nb: 'Anonym' });

  const rows = useMemo(() => {
    const labels = {
      anonymous: anonymousLabel,
      otherRole: t({ en: 'Other', nb: 'Annet' }),
      noAssignment: t({ en: 'No CV assigned', nb: 'Ingen CV tildelt' }),
    };
    const search = debouncedSearchQuery.trim().toLowerCase();
    const filtered = allRows.filter(
      (row) =>
        rowMatchesFilters(row, cvFilter.filters ?? [], duplicateCvKeys, pending, allCategories) &&
        [
          getOverviewRowCvName(row, labels.anonymous),
          getOverviewRowRoleTitle(row, allCategories, labels.otherRole, labels.noAssignment),
        ].some((text) => text.toLowerCase().includes(search)),
    );
    const visible = cvFilter.showUnassignedRoles
      ? filtered
      : filtered.filter((row) => row.kind !== 'gap');

    return sortCvOverviewRows(visible, cvFilter.order, allCategories, pending, labels);
  }, [
    allRows,
    allCategories,
    anonymousLabel,
    cvFilter,
    debouncedSearchQuery,
    duplicateCvKeys,
    pending,
  ]);

  const pendingChangeCount = countPendingChanges(initialAssignments, pending);

  const columns = useMemo<ColumnDef<CvOverviewRow>[]>(
    () => [
      {
        id: 'cv',
        header: () => t({ en: 'CV', nb: 'CV' }),
        cell: ({ row }) => {
          if (row.original.kind === 'gap') return null;

          const { representative, cvKey } = row.original;
          const name = representative.candidateName?.length
            ? representative.candidateName
            : anonymousLabel;

          return (
            <div className="flex min-w-0 items-center gap-2">
              <Badge size="xs" kind="icon" className="size-6 shrink-0">
                <FileIcon fileName="cv.pdf" />
              </Badge>
              <span className="line-clamp-1 text-sm">{name}</span>
              {duplicateCvKeys.has(cvKey) ? (
                <Badge variant="warning" size="xxs" className="shrink-0">
                  <Icon icon={Copy01Icon} />
                  {t({ en: 'Duplicate role', nb: 'Duplikat rolle' })}
                </Badge>
              ) : null}
            </div>
          );
        },
        size: 200,
        meta: { primary: true },
      },
      {
        id: 'role',
        header: () => t({ en: 'Role', nb: 'Rolle' }),
        cell: ({ row }) => {
          if (row.original.kind === 'gap') {
            return (
              <span className="font-medium text-sm">
                {row.original.category.tenderCriteriaPosition.positionTitle}
              </span>
            );
          }

          return (
            <CvRoleMenu
              cvKey={row.original.cvKey}
              assignedCategoryId={row.original.assignedCategoryId}
            />
          );
        },
        size: 320,
      },
      {
        id: 'score',
        header: () => t({ en: 'Coverage score', nb: 'Dekningsscore' }),
        cell: ({ row }) => {
          if (row.original.kind === 'gap')
            return (
              <span className="text-muted text-sm">
                {t({ en: 'No CV match!', nb: 'Ingen CV-treff!' })}
              </span>
            );

          const score = getCvAssignmentState(row.original.cvKey, pending, allCategories).score;

          if (score == null)
            return (
              <span className="text-muted text-sm">
                {t({ en: 'No CV match!', nb: 'Ingen CV-treff!' })}
              </span>
            );

          return (
            <span className="inline-flex items-center gap-1.5 text-sm font-medium tabular-nums text-foreground">
              <ScoreDial value={score} color={getBidAssessmentColor(score)} />
              {score.toFixed(1)}
            </span>
          );
        },
        size: 120,
      },
    ],
    [allCategories, anonymousLabel, duplicateCvKeys, pending],
  );

  const table = useReactTable({
    columns,
    data: rows,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) =>
      row.kind === 'cv' ? `cv-${row.cvKey}` : `gap-${row.category.tenderCriteriaPosition.id}`,
  });

  const roleCategories = getRoleCategories(allCategories);
  const hasOverviewData = getAllBidCvs(allCategories).length > 0 || roleCategories.length > 0;

  return (
    <>
      <Header>
        <HeaderBackButton />
        <BidBreadcrumbs>
          <BreadcrumbPage>{t({ en: 'CV quality check', nb: 'CV-kvalitetssjekk' })}</BreadcrumbPage>
        </BidBreadcrumbs>
        <HeaderChatButton />
      </Header>
      <MainScrollArea>
        <IndexingStatusAlerts
          resourceId={bidId}
          documentType="bid"
          stale={versionStatus === 'stale' && !isProcessing}
          onStaleRerun={() => runCvAnalysis.mutate(undefined)}
          staleRerunPending={runCvAnalysis.isPending}
        />
        {isLoading ? (
          <Spinner kind="inset" />
        ) : isLoadError || (!isProcessing && (isFailed || isNotStarted)) ? (
          <MainContainer>
            <PageTitle />
            <Alert variant={isNotStarted ? 'info' : 'destructive'}>
              <Icon icon={isNotStarted ? InformationCircleIcon : Alert01Icon} />
              <AlertDescription>
                {isLoadError
                  ? t({
                      en: 'Could not load the CV analysis. Try again.',
                      nb: 'Kunne ikke laste CV-analysen. Prøv igjen.',
                    })
                  : isNotStarted
                    ? t({
                        en: 'No CV analysis yet. Run a quality check to assess the uploaded CVs.',
                        nb: 'Ingen CV-analyse ennå. Kjør en kvalitetskontroll for å vurdere de opplastede CV-ene.',
                      })
                    : t(failureMessage)}
              </AlertDescription>
            </Alert>
            <Button
              className="mt-4"
              disabled={canStartAnalysis ? !canRun || runCvAnalysis.isPending : isFetching}
              onClick={() => {
                if (canStartAnalysis) return runCvAnalysis.mutate(undefined);
                refetch();
              }}
            >
              {isNotStarted
                ? t({ en: 'Run quality check', nb: 'Kjør kvalitetssjekk' })
                : t({ en: 'Try again', nb: 'Prøv igjen' })}
            </Button>
          </MainContainer>
        ) : isProcessing ? (
          <MainContainer>
            <PageTitle />
            <Empty>
              <EmptyMedia variant="icon">
                <Icon icon={SearchingIcon} />
              </EmptyMedia>
              <EmptyContent>
                <EmptyTitle>{t({ en: 'CV quality check', nb: 'CV-kvalitetssjekk' })}</EmptyTitle>
                <Progress value={+(data?.progress ?? 0).toFixed(0)} max={100}>
                  <EmptyDescription>
                    {t({ en: 'Analysing', nb: 'Analyserer' })} <ProgressValue />
                  </EmptyDescription>
                  <ProgressTrack className="md:absolute md:bottom-0 md:rounded-none md:left-0 w-full" />
                </Progress>
              </EmptyContent>
            </Empty>
          </MainContainer>
        ) : (
          <>
            <MainContainer>
              <PageTitle />
              <Alert variant="info" className="mb-5">
                <Icon icon={InformationCircleIcon} />
                <AlertDescription>
                  {t({
                    en: 'This analysis focuses explicitly on CVs. Pricing and execution plan are not taken into consideration.',
                    nb: 'Denne analysen fokuserer eksplisitt på CV-er. Prising og gjennomføringsplan tas ikke i betraktning.',
                  })}
                </AlertDescription>
              </Alert>
              {data?.summary && (
                <Card className="p-4">
                  <div
                    data-score={hasTotalScore ? '' : undefined}
                    className="flex flex-col gap-6 md:flex-row md:items-start md:gap-6 data-score:items-center md:data-score:items-stretch"
                  >
                    <div
                      data-score={hasTotalScore ? '' : undefined}
                      className="flex shrink-0 flex-col gap-2 @4xl:data-score:my-auto md:data-score:min-w-30 md:data-score:gap-0"
                    >
                      {hasTotalScore ? (
                        <div className="flex w-full items-center justify-center md:min-h-0 mb-3">
                          <CircularProgress
                            max={10}
                            getValueText={(value) => value.toFixed(1)}
                            value={data.totalScore}
                            size={100}
                            label={t({ en: 'Coverage score', nb: 'Dekningsscore' })}
                            color={getBidAssessmentColor(data.totalScore)}
                          />
                        </div>
                      ) : null}
                      {showScoredCount ? (
                        <p className="w-max mx-auto max-w-full shrink-0 text-center text-xs text-muted">
                          {t({
                            en: (
                              <>
                                <span className="font-medium text-foreground">
                                  {scoredCvCounts.scored}
                                </span>{' '}
                                of{' '}
                                <span className="font-medium text-foreground">
                                  {scoredCvCounts.total}
                                </span>{' '}
                                assessments scored
                              </>
                            ),
                            nb: (
                              <>
                                <span className="font-medium text-foreground">
                                  {scoredCvCounts.scored}
                                </span>{' '}
                                av{' '}
                                <span className="font-medium text-foreground">
                                  {scoredCvCounts.total}
                                </span>{' '}
                                vurderinger scoret
                              </>
                            ),
                          })}
                        </p>
                      ) : null}
                    </div>
                    <div className="min-w-0 w-full flex-1">
                      <AISummaryAlert text={data.summary}>
                        <AISummaryAlertTitle>
                          <AISummaryAlertCopyButton />
                          <AISummaryAlertAskAIButton />
                        </AISummaryAlertTitle>
                        <AISummaryAlertDescription />
                      </AISummaryAlert>
                    </div>
                  </div>
                </Card>
              )}
            </MainContainer>
            {!hasOverviewData ? (
              <p className="px-x py-4 text-sm text-muted">
                {t({
                  en: 'No role requirements found. Run the CV quality check after uploading CVs and the organization chart.',
                  nb: 'Ingen rollekrav funnet. Kjør CV-kvalitetssjekken etter at du har lastet opp CV-er og organisasjonskart.',
                })}
              </p>
            ) : (
              <>
                <ActionBar
                  open={pendingChangeCount > 0}
                  onOpenChange={(open) => {
                    if (!open) setPending(initialAssignments);
                  }}
                >
                  <ActionBarSelection>
                    {t({
                      en:
                        pendingChangeCount === 1
                          ? `${pendingChangeCount} role changed`
                          : `${pendingChangeCount} roles changed`,
                      nb:
                        pendingChangeCount === 1
                          ? `${pendingChangeCount} rolle endret`
                          : `${pendingChangeCount} roller endret`,
                    })}
                    <ActionBarSeparator />
                    <ActionBarXClose />
                  </ActionBarSelection>
                  <ActionBarSeparator />
                  <ActionBarGroup>
                    <ActionBarItem
                      disabled={runCvAnalysis.isPending || hasDuplicates || roleEditingBlocked}
                      onSelect={() => setRerunOpen(true)}
                    >
                      <Icon icon={Refresh01Icon} />
                      {t({ en: 'Rerun CV analysis', nb: 'Kjør CV-analyse på nytt' })}
                    </ActionBarItem>
                  </ActionBarGroup>
                </ActionBar>
                <AlertDialog open={rerunOpen} onOpenChange={setRerunOpen}>
                  <AlertDialogPopup>
                    <AlertDialogDescription>
                      {t(common.reRunAnalysisDescription)}
                    </AlertDialogDescription>
                    <AlertDialogFooter>
                      <AlertDialogCancel />
                      <Button
                        pending={runCvAnalysis.isPending}
                        onClick={() =>
                          runCvAnalysis.mutate(
                            { assignments: buildAssignmentPayload(pending, allCategories) },
                            { onSuccess: () => setRerunOpen(false) },
                          )
                        }
                      >
                        {t(common.reRunAnalysis)}
                      </Button>
                    </AlertDialogFooter>
                  </AlertDialogPopup>
                </AlertDialog>
                <div className="px-x mb-4">
                  <H2>{t({ en: 'Role requirement mapping', nb: 'Kartlegging mot rollekrav' })}</H2>
                  <p className="text-sm text-muted">
                    {t({
                      en: 'Map CV content to the role requirements listed in the RFP (per position/role).',
                      nb: 'Kartlegger CV-innhold mot rollekravene som er listet i forespørselen (per stilling/rolle).',
                    })}
                  </p>
                </div>
                {roleEditingBlocked && (
                  <div className="px-x mb-4">
                    <Alert variant="info">
                      <Icon icon={InformationCircleIcon} />
                      <AlertDescription>
                        {t({
                          en: 'Role editing is unavailable for CV files assessed against multiple roles.',
                          nb: 'Roller kan ikke endres for CV-filer som er vurdert mot flere roller.',
                        })}
                      </AlertDescription>
                    </Alert>
                  </div>
                )}
                {duplicateRoleGroups.length > 0 ? (
                  <div className="px-x mb-4 flex flex-col gap-2">
                    {duplicateRoleGroups.map((group) => (
                      <Alert key={group.categoryId} variant="warning">
                        <AlertDescription>
                          <DuplicateRoleBannerDescription
                            group={group}
                            allCategories={allCategories}
                            anonymousLabel={anonymousLabel}
                          />
                        </AlertDescription>
                      </Alert>
                    ))}
                  </div>
                ) : null}
                <div className="flex flex-col grow pb-20">
                  <MainActions className="mb-0 border-b">
                    <CvAnalysisFilterMenu />
                    <MainActionsEnd>
                      <MainActionsSearchInput
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                      <CvAnalysisDisplayMenu />
                    </MainActionsEnd>
                  </MainActions>
                  {rows.length === 0 && (!!debouncedSearchQuery || !!cvFilter.filters?.length) ? (
                    <Empty className="relative pt-20">
                      <EmptyContent>
                        <EmptyTitle>{t(common.noResults)}</EmptyTitle>
                        <EmptyDescription>{t(common.noResultsDescription)}</EmptyDescription>
                        <Button
                          variant="secondary"
                          onClick={() => {
                            setSearchQuery('');
                            setCvFilter((prev) => ({ ...prev, filters: undefined }));
                          }}
                        >
                          {t(common.clearFilters)}
                        </Button>
                      </EmptyContent>
                    </Empty>
                  ) : (
                    <Table>
                      <DataTableHeader table={table} />
                      <TableBody>
                        {table.getRowModel().rows.map((row) => (
                          <TableRow
                            key={row.id}
                            data-clickable={row.original.kind === 'cv' ? '' : undefined}
                            className="h-11 group/row data-clickable:cursor-pointer"
                            onClick={() => {
                              if (row.original.kind !== 'cv') return;
                              const position = getNavigationPositionForBidDoc(
                                row.original.cvKey,
                                pending,
                                allCategories,
                              );
                              if (!position) return;
                              navigate({
                                to: '/project/$projectId/workspaces/$workspaceId/bids/$bidId/cv-analysis/$positionId',
                                params: { projectId, workspaceId, bidId, positionId: position.id },
                              });
                            }}
                          >
                            {row.getVisibleCells().map((cell) => (
                              <DataTableCell key={cell.id} cell={cell} />
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </MainScrollArea>
    </>
  );
}

function PageTitle() {
  const { bidId, workspaceId, projectId } = Route.useParams();
  return (
    <H1 className="mb-6 z-25 relative">
      <MainHeaderBackButton
        to="/project/$projectId/workspaces/$workspaceId/bids/$bidId"
        params={{
          projectId,
          workspaceId,
          bidId,
        }}
      />
      {t({ en: 'CVs quality check', nb: 'CV-kvalitetssjekk' })}
    </H1>
  );
}

function DuplicateRoleBannerDescription({
  group,
  allCategories,
  anonymousLabel,
}: {
  group: DuplicateRoleGroup;
  allCategories: CvAssessmentCategory[];
  anonymousLabel: string;
}) {
  const names = group.cvKeys.map((cvKey) => getCvDisplayName(cvKey, allCategories, anonymousLabel));
  const role = <span className="font-semibold text-foreground">{group.roleTitle}</span>;

  const [nameA, nameB] = names;
  if (names.length === 2 && nameA !== undefined && nameB !== undefined) {
    const boldA = <span className="font-semibold text-foreground">{nameA}</span>;
    const boldB = <span className="font-semibold text-foreground">{nameB}</span>;
    return t({
      en: (
        <>
          {boldA} and {boldB} are both mapped to {role} required by the RFP.
        </>
      ),
      nb: (
        <>
          {boldA} og {boldB} er begge tilordnet {role}, som kreves i anbudsdokumentet.
        </>
      ),
    });
  }

  return (
    <>
      {names.map((name, index) => (
        <Fragment key={name}>
          {index > 0
            ? index === names.length - 1
              ? ` ${t({ en: 'and', nb: 'og' })} `
              : ', '
            : null}
          <span className="font-semibold text-foreground">{name}</span>
        </Fragment>
      ))}{' '}
      {t({
        en: <>are both mapped to {role} required by the RFP.</>,
        nb: <>er begge tilordnet {role}, som kreves i anbudsdokumentet.</>,
      })}
    </>
  );
}
